import { FC, useState } from "react";

import StepIntroDesktop from "../../../StepIntroDesktop";
import StepDeviceSelect from "../../../StepDeviceSelect";
import StepQRCode from "../../../StepQRCode";
import StepBelly from "../../../StepBelly";
import StepTorse from "../../../StepTorse";
import StepCuisses from "../../../StepCuisses";
import StepBreasts from "../../../StepBreasts";
import ResultError from "../../../ResultError";
import Result from "../../../Result";
import ConsentScreen from "../../../ConsentScreen";
import StepSleeves from "../../../StepSleeves";

import { GENDERS, MODAL_STEPS } from "../../../../constants/modal";
import {
  SimilarProductType,
  RecommendedSizeType,
} from "../../../../types/result";

import { removeLocalStore } from "../../../../store/localStoreUtils";
import {
  subtitlesStyles,
  uxGender,
  uxRoute,
  uxRouteDefault,
  uxRouteNone,
  uxRouteQuestionOnly,
  uxRouteQuestionPushScan,
} from "../../../../configs";
import { useUserContext } from "../../../../store/userContext";
import { handleAnalytics } from "../../../../utils/tracking";
import { postClearMid } from "../../../../utils/post";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { INCHtoCM, LBStoKG, logger, useIsMobile } from "../../../../utils";
import LingerieSteps from "./components/LingerieSteps";
import ShoeSteps from "./components/ShoeSteps";
import StepShoeGender from "../StepShoeGender/StepShoeGender";
import { visionInit } from "../../../../api/endpoints";

import "./index.css";

interface IPropsDrawerSteps {
  step: {
    number: number;
  } | null;
  defaultStep: {
    number: number;
  } | null;
  error: any;
  height: string;
  unit: string;
  feet: string;
  inches: string;
  unitWeight: string;
  weight?: string | undefined;
  age: string;
  selectedGender: string;
  selectedShoeGender: string | null;
  setSelectedShoeGender: React.Dispatch<React.SetStateAction<string | null>>;
  disableContinue: boolean;
  bellyValue: any;
  torsoValue: any;
  cuissesValue: any;
  sizeValue: any;
  cupValue: any;
  sleeveValue: { current?: number };
  ageChildrenYears: string;
  ageChildrenMonths: string;
  unitAge: string;
  handleUnitAgeChange: (newUnit: string) => void;
  sendQuestionAnswers: (isSkipResult?: boolean) => void;
  handleFieldChange: (e: any, type: string, blurSignal: string) => void;
  handleUnitChange: (newUnit: string) => void;
  handleUnitWeightChange: (newUnit: string) => void;
  handleGenderSelect: (newUnit: string) => void;
  setNeedValidate: React.Dispatch<React.SetStateAction<boolean>>;
  recommendedSize: RecommendedSizeType[];
  setRecommendedSize: React.Dispatch<RecommendedSizeType[] | null>;
  reducedResult: any;
  selectedRoute: string;
  setSelectedRoute: React.Dispatch<React.SetStateAction<string>>;
  sizeCountry: string;
  setSizeCountry: React.Dispatch<React.SetStateAction<string>>;
  productStockData: any;
  variantId: string | null;
  variantCol: string | null;
  similarProducts: SimilarProductType[] | null;
  isSizeUnavailable: boolean;
  selectedSize: RecommendedSizeType | null;
  setSelectedSize: React.Dispatch<
    React.SetStateAction<RecommendedSizeType | null>
  >;
  setSimilarProducts: React.Dispatch<SimilarProductType[] | null>;
  setStep: (
    value: React.SetStateAction<{
      number: number;
    } | null>,
  ) => void;
  setPreviousStep: React.Dispatch<
    React.SetStateAction<{
      number: number;
    } | null>
  >;
  restart: () => void;
  setIsScanError: React.Dispatch<React.SetStateAction<boolean>>;
  outOfRange: boolean;
  isChildren: boolean;
  shoeQuestion: string | null;
  setShoeQuestion: React.Dispatch<React.SetStateAction<string | null>>;
  policyChecked: boolean;
  setPolicyChecked: React.Dispatch<React.SetStateAction<boolean>>;
  lingerieQuestion1: string | null;
  setLingerieQuestion1: React.Dispatch<React.SetStateAction<string | null>>;
  lingerieQuestion2: string | null;
  setLingerieQuestion2: React.Dispatch<React.SetStateAction<string | null>>;
  lingerieQuestion3: string | null;
  setLingerieQuestion3: React.Dispatch<React.SetStateAction<string | null>>;
  lingerieQuestion4: string | null;
  setLingerieQuestion4: React.Dispatch<React.SetStateAction<string | null>>;
  titlesStyles: React.CSSProperties;
  descriptionStyles: React.CSSProperties;
  company: string;
  fieldsValidation: () => boolean;
  setDisableContinue: React.Dispatch<React.SetStateAction<boolean>>;
  setTouchedCompany: React.Dispatch<React.SetStateAction<boolean>>;
  setFeetTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setInchesTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

const DrawerSteps: FC<IPropsDrawerSteps> = ({
  step,
  defaultStep,
  error,
  height,
  unit,
  feet,
  inches,
  unitWeight,
  weight,
  age,
  selectedGender,
  selectedShoeGender,
  setSelectedShoeGender,
  disableContinue,
  bellyValue,
  torsoValue,
  cuissesValue,
  sizeValue,
  cupValue,
  sleeveValue,
  sendQuestionAnswers,
  handleFieldChange,
  handleUnitChange,
  handleUnitWeightChange,
  handleGenderSelect,
  setNeedValidate,
  recommendedSize,
  setRecommendedSize,
  reducedResult,
  selectedRoute,
  setSelectedRoute,
  sizeCountry,
  setSizeCountry,
  productStockData,
  variantId,
  variantCol,
  similarProducts,
  isSizeUnavailable,
  selectedSize,
  setSelectedSize,
  setStep,
  setPreviousStep,
  restart,
  setSimilarProducts,
  setIsScanError,
  outOfRange,
  isChildren,
  ageChildrenYears,
  ageChildrenMonths,
  unitAge,
  handleUnitAgeChange,
  shoeQuestion,
  setShoeQuestion,
  policyChecked,
  setPolicyChecked,
  lingerieQuestion1,
  setLingerieQuestion1,
  lingerieQuestion2,
  setLingerieQuestion2,
  lingerieQuestion3,
  setLingerieQuestion3,
  lingerieQuestion4,
  setLingerieQuestion4,
  titlesStyles,
  descriptionStyles,
  company,
  fieldsValidation,
  setDisableContinue,
  setTouchedCompany,
  setFeetTouched,
  setInchesTouched,
}) => {
  const isMobile = useIsMobile();
  const userContext = useUserContext();

  const urlParameters = new URLSearchParams(window.location.search);
  const domain = urlParameters.get("domain");
  const mock = urlParameters.get("mock") === "true";

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFootwear =
    urlParameters.get("category") === "footwear" ||
    urlParameters.get("footwear") === "true";
  const isLingerie =
    urlParameters.get("category") === "lingerie" ||
    urlParameters.get("lingerie") === "true";

  const brandDefined = findBrandByDomain();

  const isHast = brandDefined?.name === "Hast";

  const isFirstIntroScreen =
    step?.number === MODAL_STEPS.INTRO.number ||
    step?.number === MODAL_STEPS.GENDER.number ||
    step?.number === MODAL_STEPS.INTRO_MOBILE.number;

  const handleRouteEvent = async (type: string) => {
    if (type === "question_pushScan") {
      if (!userContext) return;

      if (isFootwear && isMobile) {
        openScan();
      } else {
        const resultNewMeasure = await userContext.createNewMeasure("scan");
        if (resultNewMeasure) {
          setStep(MODAL_STEPS.QR_CODE);
        }
      }
    }

    if (type === "question_only") {
      if (isFootwear) {
        setStep(!uxGender ? MODAL_STEPS.SHOE_GENDER : MODAL_STEPS.BREASTS); // need to change
      } else {
        setStep(MODAL_STEPS.BELLY);
      }
    }

    if (type === "") {
      setStep(MODAL_STEPS.BELLY);
    }

    setSelectedRoute(type);
  };

  const handleRetry = async () => {
    if (!userContext || !domain) return;

    if (isLingerie) return restart();

    if (isFootwear) {
      ["mid", "uid", "gender"].forEach(removeLocalStore);
      setStep(MODAL_STEPS.DEVICE_SELECT);
      setIsScanError(false);
      return restart();
    }

    ["mid", "uid", "gender"].forEach(removeLocalStore);

    const newUserId = await userContext.createNewUser(domain);
    if (!newUserId) {
      return restart();
    }

    if (uxRouteNone || outOfRange) {
      if (userContext?.refreshContext) {
        userContext.refreshContext();
      }

      return restart();
    }

    const routes: Record<string, "scan" | "question"> = {
      question_pushScan: "scan",
      question_only: "question",
    };

    const measureType = routes[selectedRoute];
    if (!measureType) {
      logger.log(`route not recognized ${selectedRoute}`);
      return restart();
    }

    const resultNewMeasure = await userContext.createNewMeasure(measureType);
    if (!resultNewMeasure) {
      return restart();
    }

    setStep(measureType === "scan" ? MODAL_STEPS.QR_CODE : defaultStep);

    handleAnalytics("action", "error_restart", {
      key: "route_selected",
      value: measureType,
    });
  };

  const handleSaveUserInfo = async () => {
    const body = {
      gender: selectedGender,
      age,
      height: unit === "feet" ? INCHtoCM(`${feet} ${inches}`) : height,
      weight:
        unitWeight === "lbs" && weight ? LBStoKG(parseInt(weight)) : weight,
    };
    localStorage.setItem("user", JSON.stringify(body));
  };

  const clearMid = () => {
    postClearMid();
  };

  const openScan = () => {
    const scanUrl = userContext?.shoeWebAppURL;

    if (!!scanUrl) {
      handleAnalytics("action", "scan_clicked", null);

      window.open(scanUrl, "_blank", "noopener,noreferrer");

      userContext?.handleCheckShoeRecommend();
    }
  };

  const nextStepBreasts = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (isLingerie) {
        setStep(MODAL_STEPS.LINGERIE_QUESTION_1);
        return;
      }

      if (isFootwear) {
        if (uxRouteNone) {
          setStep(MODAL_STEPS.RESULT);

          if (userContext) {
            await userContext.createNewMeasure("question");
            await sendQuestionAnswers();
          }
        } else {
          setStep(MODAL_STEPS.SHOE_QUESTIONS);
        }

        return;
      }

      if (!isFootwear && !isLingerie) {
        if (userContext) {
          await userContext.createNewMeasure("question");
          await sendQuestionAnswers();
        }
      }
    } catch (error) {
      logger.error("Find my ideal size failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {step?.number === MODAL_STEPS.CONSENT.number && (
        <ConsentScreen
          step={step}
          nextStep={() => {
            handleAnalytics("action", "consent_submit", null);
            setStep(
              isMobile
                ? uxGender
                  ? MODAL_STEPS.INTRO_MOBILE
                  : MODAL_STEPS.GENDER
                : MODAL_STEPS.INTRO,
            );
          }}
          clearMid={clearMid}
          descriptionStyles={descriptionStyles}
        />
      )}
      {isFirstIntroScreen && (
        <StepIntroDesktop
          step={step}
          error={error}
          height={height}
          unit={unit}
          feet={feet}
          inches={inches}
          unitWeight={unitWeight}
          weight={weight}
          age={age}
          unitAge={unitAge}
          selectedGender={selectedGender}
          ageChildrenYears={ageChildrenYears}
          ageChildrenMonths={ageChildrenMonths}
          disableContinue={disableContinue}
          handleFieldChange={handleFieldChange}
          handleUnitChange={handleUnitChange}
          handleUnitWeightChange={handleUnitWeightChange}
          handleGenderSelect={handleGenderSelect}
          setNeedValidate={setNeedValidate}
          titlesStyles={titlesStyles}
          descriptionStyles={descriptionStyles}
          handleUnitAgeChange={handleUnitAgeChange}
          policyChecked={policyChecked}
          setPolicyChecked={setPolicyChecked}
          company={company}
          setTouchedCompany={setTouchedCompany}
          setFeetTouched={setFeetTouched}
          setInchesTouched={setInchesTouched}
          nextStepGender={async () => {
            if (
              !uxGender &&
              isMobile &&
              step?.number === MODAL_STEPS.GENDER.number
            ) {
              setStep(MODAL_STEPS.INTRO_MOBILE);
            }
          }}
          nextStep={async () => {
            const hasErrors = fieldsValidation();

            if (hasErrors) {
              setDisableContinue(true);
              return;
            }

            if (isChildren && userContext) {
              await userContext.createNewMeasure("question");
              sendQuestionAnswers();
            } else {
              if (uxRouteNone && userContext) {
                const resultNewMeasure =
                  await userContext.createNewMeasure("question");
                if (resultNewMeasure) {
                  if (selectedGender === GENDERS.M) {
                    sendQuestionAnswers();
                  } else if (selectedGender === GENDERS.F) {
                    setStep(MODAL_STEPS.BREASTS);
                  }
                }
              }

              if (
                uxRouteQuestionOnly ||
                uxRouteQuestionPushScan ||
                uxRoute === ""
              ) {
                await handleRouteEvent(uxRoute);
              }

              // desktop first screen
              if (
                uxRouteDefault &&
                [
                  MODAL_STEPS.INTRO.number,
                  MODAL_STEPS.INTRO_MOBILE.number,
                ].includes(step?.number)
              ) {
                setStep(MODAL_STEPS.DEVICE_SELECT);
              }
            }

            handleSaveUserInfo();
          }}
        />
      )}
      {step?.number === MODAL_STEPS.DEVICE_SELECT.number && (
        <StepDeviceSelect
          step={step}
          nextPhotoStep={async () =>
            await handleRouteEvent("question_pushScan")
          }
          nextQuestionsStep={async () =>
            await handleRouteEvent("question_only")
          }
        />
      )}
      {step?.number === MODAL_STEPS.QR_CODE.number && (
        <StepQRCode
          step={step}
          gender={selectedGender}
          setRecommendedSize={setRecommendedSize}
          titleStyles={titlesStyles}
          descriptionStyles={descriptionStyles}
          productStockData={productStockData}
          variantId={variantId}
          variantCol={variantCol}
          skipToResult={() => setStep(MODAL_STEPS.RESULT)}
          handleError={() => setStep(MODAL_STEPS.ERROR)}
          setIsScanError={setIsScanError}
        />
      )}
      {step?.number === MODAL_STEPS.SHOE_GENDER.number && (
        <StepShoeGender
          selectedShoeGender={selectedShoeGender}
          setSelectedShoeGender={setSelectedShoeGender}
          nextStep={() => setStep(MODAL_STEPS.BREASTS)}
          subtitlesStyles={subtitlesStyles}
        />
      )}
      {step?.number === MODAL_STEPS.BELLY.number && (
        <StepBelly
          step={step}
          gender={selectedGender}
          nextStep={() => setStep(MODAL_STEPS.TORSO)}
          setStep={setStep}
          setPreviousStep={setPreviousStep}
          value={bellyValue}
          descriptionStyles={descriptionStyles}
        />
      )}
      {step?.number === MODAL_STEPS.TORSO.number && (
        <StepTorse
          step={step}
          gender={selectedGender}
          nextStep={() => {
            if (selectedGender === GENDERS.M) {
              setStep(MODAL_STEPS.CUISSES);
            } else {
              setStep(MODAL_STEPS.BREASTS);
            }
          }}
          setStep={setStep}
          setPreviousStep={setPreviousStep}
          value={torsoValue}
          descriptionStyles={descriptionStyles}
        />
      )}
      {step?.number === MODAL_STEPS.CUISSES.number && (
        <StepCuisses
          step={step}
          gender={selectedGender}
          value={cuissesValue}
          nextStep={async () => {
            if (isHast) {
              setStep(MODAL_STEPS.SLEEVES);
            } else {
              if (userContext) {
                await userContext.createNewMeasure("question");
                sendQuestionAnswers();
              }
            }
          }}
          setStep={setStep}
          setPreviousStep={setPreviousStep}
          descriptionStyles={descriptionStyles}
        />
      )}
      {step?.number === MODAL_STEPS.SLEEVES.number && (
        <StepSleeves
          step={step}
          gender={selectedGender}
          value={sleeveValue}
          nextStep={async () => {
            if (userContext) {
              await userContext.createNewMeasure("question");
              sendQuestionAnswers();
            }
          }}
          setStep={setStep}
          setPreviousStep={setPreviousStep}
          descriptionStyles={descriptionStyles}
        />
      )}
      {step?.number === MODAL_STEPS.BREASTS.number && (
        <StepBreasts
          step={step}
          value_1={sizeValue}
          value_2={cupValue}
          selectedGender={selectedGender}
          isSubmitting={isSubmitting}
          nextStep={nextStepBreasts}
          skipResult={async () => {
            if (isFootwear) {
              setStep(MODAL_STEPS.SHOE_QUESTIONS);
            } else {
              if (userContext) {
                await userContext.createNewMeasure("question");
                sendQuestionAnswers(true);
              }
            }
          }}
          sizeCountry={sizeCountry}
          setSizeCountry={setSizeCountry}
          descriptionStyles={descriptionStyles}
        />
      )}

      {/* Custom steps */}
      <LingerieSteps
        step={step}
        setStep={setStep}
        subtitlesStyles={subtitlesStyles}
        sendQuestionAnswers={sendQuestionAnswers}
        lingerieQuestion1={lingerieQuestion1}
        setLingerieQuestion1={setLingerieQuestion1}
        lingerieQuestion2={lingerieQuestion2}
        setLingerieQuestion2={setLingerieQuestion2}
        lingerieQuestion3={lingerieQuestion3}
        setLingerieQuestion3={setLingerieQuestion3}
        lingerieQuestion4={lingerieQuestion4}
        setLingerieQuestion4={setLingerieQuestion4}
      />

      <ShoeSteps
        step={step}
        shoeQuestion={shoeQuestion}
        setShoeQuestion={setShoeQuestion}
        setStep={setStep}
        subtitlesStyles={subtitlesStyles}
        sendQuestionAnswers={sendQuestionAnswers}
        mock={mock}
        domain={domain}
        visionInit={visionInit}
      />

      {step?.number === MODAL_STEPS.ERROR.number && (
        <ResultError
          step={step}
          nextStep={async () => await handleRetry()}
          outOfRange={outOfRange}
        />
      )}
      {(step?.number === MODAL_STEPS.RESULT.number ||
        step?.number === MODAL_STEPS.UNAVAILABLE_RESULT.number) && (
        <Result
          step={step}
          reducedResult={reducedResult}
          selectedGender={selectedGender}
          productStockData={productStockData}
          similarProducts={similarProducts}
          isSizeUnavailable={isSizeUnavailable}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          restart={restart}
          setSimilarProducts={setSimilarProducts}
          recommendedSize={recommendedSize}
        />
      )}
    </>
  );
};

export default DrawerSteps;
